import React, {
    FC, useCallback, useEffect, useRef, useState,
} from 'react';

import { Button, Input, Popover, type InputRef } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PhoneInput, { getCountryCallingCode, isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import flags from 'react-phone-number-input/flags';

import { PhoneIdentity } from '@/types/types';
import styles from './EditableCellPopup.module.scss';

interface PhoneCellPopupProps {
    type: 'phone';
    phone?: string;
    prefix?: string;
    onSave: (phoneNumber: PhoneIdentity) => Promise<void>;
}

interface PasswordCellPopupProps {
    type: 'password';
    maskedPassword?: string | null;
    hasPassword?: boolean;
    onSave: (password: string) => Promise<void>;
}

/** `cellTestId` is listed on each union branch so JSX usage type-checks reliably (not only via `&` with `PhoneCellPopupProps`). */
export type EditableCellPopupProps =
    | (PhoneCellPopupProps & {
        displayValue: React.ReactNode;
        isEmpty?: boolean;
        cellTestId?: string;
    })
    | (PasswordCellPopupProps & {
        displayValue: React.ReactNode;
        isEmpty?: boolean;
        cellTestId?: string;
    });

function EditableCellPopup(props: EditableCellPopupProps): React.ReactElement {
    const { type, displayValue, isEmpty, cellTestId } = props;
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const handleCancel = useCallback(() => {
        setOpen(false);
    }, []);

    const content = type === 'phone'
        ? (
            <PhonePopupContent
                phone={(props as PhoneCellPopupProps).phone}
                prefix={(props as PhoneCellPopupProps).prefix}
                onSave={async (val) => {
                    setSaving(true);
                    await (props as PhoneCellPopupProps).onSave(val);
                    setSaving(false);
                    setOpen(false);
                }}
                onCancel={handleCancel}
                saving={saving}
                popupTestId={cellTestId}
            />
        )
        : (
            <PasswordPopupContent
                maskedPassword={(props as PasswordCellPopupProps).maskedPassword}
                onSave={async (val) => {
                    setSaving(true);
                    await (props as PasswordCellPopupProps).onSave(val);
                    setSaving(false);
                    setOpen(false);
                }}
                onCancel={handleCancel}
                saving={saving}
                popupTestId={cellTestId}
            />
        );

    return (
        <Popover
            content={content}
            trigger="click"
            open={open}
            onOpenChange={setOpen}
            placement="bottomLeft"
            overlayClassName={styles['editable-cell-popover']}
            destroyTooltipOnHide
        >
            <div className={styles['editable-cell']} data-testid={cellTestId}>
                <span className={styles['editable-cell-value']}>
                    {displayValue}
                </span>
                <EditOutlined className={styles['editable-cell-icon']} />
                {isEmpty && (
                    <EditOutlined
                        className={styles['editable-cell-icon-empty']}
                        onClick={() => setOpen(true)}
                    />
                )}
            </div>
        </Popover>
    );
}

/* ─── Phone popup ─── */

interface PhonePopupContentProps {
    phone?: string;
    prefix?: string;
    onSave: (value: PhoneIdentity) => Promise<void>;
    onCancel: () => void;
    saving: boolean;
    popupTestId?: string;
}

const PhonePopupContent: FC<PhonePopupContentProps> = ({
    phone, prefix, onSave, onCancel, saving, popupTestId,
}) => {
    const { t } = useTranslation();
    const [phoneValue, setPhoneValue] = useState<string | undefined>(() => {
        if (phone && prefix) return `+${prefix}${phone}`;
        return undefined;
    });
    const inputRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const input = inputRef.current?.querySelector('input');
        input?.focus();
    }, []);

    const isValid = phoneValue && isValidPhoneNumber(phoneValue);

    const handleApply = async (): Promise<void> => {
        if (!phoneValue || !isValid || saving) return;
        const parsed = parsePhoneNumber(phoneValue);
        if (!parsed) return;
        const callingCode = getCountryCallingCode(parsed.country);
        await onSave({
            phone: parsed.nationalNumber as string,
            prefix: String(callingCode),
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent): void => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleApply();
        }
    };

    return (
        <div
            className={styles['popup-content']}
            ref={inputRef}
            data-testid={popupTestId ? `${popupTestId}_popup` : undefined}
            onKeyDown={handleKeyDown}
        >
            <PhoneInput
                international
                defaultCountry="IL"
                flags={flags}
                value={phoneValue}
                onChange={(val) => setPhoneValue(val)}
                className={styles['popup-phone-input']}
            />
            <div className={styles['popup-buttons']}>
                <Button
                    className={styles['popup-cancel']}
                    onClick={onCancel}
                    data-testid={popupTestId ? `${popupTestId}_cancel` : undefined}
                >
                    {t('general.buttons.cancel')}
                </Button>
                <Button
                    type="primary"
                    className={styles['popup-apply']}
                    disabled={!isValid || saving}
                    loading={saving}
                    onClick={handleApply}
                    data-testid={popupTestId ? `${popupTestId}_apply` : undefined}
                >
                    {t('general.buttons.apply')}
                </Button>
            </div>
        </div>
    );
};

/* ─── Password popup ─── */

interface PasswordPopupContentProps {
    maskedPassword?: string | null;
    onSave: (password: string) => Promise<void>;
    onCancel: () => void;
    saving: boolean;
    popupTestId?: string;
}

const PasswordPopupContent: FC<PasswordPopupContentProps> = ({
    maskedPassword, onSave, onCancel, saving, popupTestId,
}) => {
    const { t } = useTranslation();
    const [password, setPassword] = useState(maskedPassword || '');
    const inputRef = useRef<InputRef>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const isValid = password.trim().length > 0;

    const handleApply = async (): Promise<void> => {
        if (!isValid || saving) return;
        await onSave(password);
    };

    return (
        <div className={styles['popup-content']} data-testid={popupTestId ? `${popupTestId}_popup` : undefined}>
            <Input.Password
                ref={inputRef}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('uploadFiles.policyStep.missingDetailsSection.enterPassword')}
                className={styles['popup-password-input']}
                onPressEnter={handleApply}
                data-testid={popupTestId ? `${popupTestId}_input` : undefined}
            />
            <div className={styles['popup-buttons']}>
                <Button
                    className={styles['popup-cancel']}
                    onClick={onCancel}
                    data-testid={popupTestId ? `${popupTestId}_cancel` : undefined}
                >
                    {t('general.buttons.cancel')}
                </Button>
                <Button
                    type="primary"
                    className={styles['popup-apply']}
                    disabled={!isValid || saving}
                    loading={saving}
                    onClick={handleApply}
                    data-testid={popupTestId ? `${popupTestId}_apply` : undefined}
                >
                    {t('general.buttons.apply')}
                </Button>
            </div>
        </div>
    );
};

export default EditableCellPopup;
